import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { COMPANY } from "@/lib/company";

const fmt = (n) => Number(n || 0).toLocaleString("en-IN");
const inr = (n) => `Rs. ${fmt(Math.round(n || 0))}`;

let _logoDataUrlPromiseR = null;
function getLogoDataUrlR() {
  if (_logoDataUrlPromiseR) return _logoDataUrlPromiseR;
  _logoDataUrlPromiseR = fetch(COMPANY.logoUrl)
    .then((r) => r.blob())
    .then((blob) => new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(blob);
    }))
    .catch(() => null);
  return _logoDataUrlPromiseR;
}

async function header(doc, title, subtitle, range) {
  const pageW = doc.internal.pageSize.getWidth();
  const margin = 40;
  const HEADER_H = 130;
  const RIGHT_BLOCK_W = 150;

  const logoData = await getLogoDataUrlR();
  if (logoData) {
    try { doc.addImage(logoData, "PNG", margin, 14, 77, 77); } catch (e) { /* noop */ }
  }

  const companyX = margin + 84;
  const companyMaxW = pageW - margin - 84;

  // Row 1: company name only + underline
  doc.setTextColor(10, 10, 10);
  doc.setFont("helvetica", "bold");
  let nameSize = 14;
  while (nameSize > 8 && doc.getTextWidth((doc.setFontSize(nameSize), COMPANY.legalName)) > companyMaxW) {
    nameSize -= 0.5;
    doc.setFontSize(nameSize);
  }
  doc.setFontSize(nameSize);
  const nameY = 32;
  doc.text(COMPANY.legalName, companyX, nameY);
  const nameWidth = doc.getTextWidth(COMPANY.legalName);
  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(0.8);
  doc.line(companyX, nameY + 3, companyX + nameWidth, nameY + 3);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const leftMaxW = pageW - margin - 84 - RIGHT_BLOCK_W - 12;
  const addrLines = doc.splitTextToSize(COMPANY.address, leftMaxW);
  const row1Y = nameY + 22;
  doc.text(addrLines, companyX, row1Y);
  const afterAddrY = row1Y + addrLines.length * 11;
  doc.text(`Contact: ${COMPANY.contact1} / ${COMPANY.contact2}   ·   Customer Care: ${COMPANY.customerCare}`, companyX, afterAddrY);
  doc.text(`${COMPANY.email}   ·   GST: ${COMPANY.gst}`, companyX, afterAddrY + 11);

  // Right-aligned report meta — same size as address rows
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.text(title, pageW - margin, row1Y, { align: "right" });
  if (range) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.text(range, pageW - margin, row1Y + 12, { align: "right" });
  }

  doc.setDrawColor(10, 10, 10);
  doc.setLineWidth(1.2);
  doc.line(margin, HEADER_H, pageW - margin, HEADER_H);

  if (subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    doc.text(subtitle, margin, HEADER_H + 18);
  }
}

export async function buildSkuReportPdf(rows, rangeLabel) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const totals = rows.reduce((acc, r) => {
    acc.qty += r.total_quantity || 0;
    acc.pcs += r.total_pcs || 0;
    acc.sets += r.total_sets || 0;
    acc.amt += r.total_amount || 0;
    acc.orders += r.order_count || 0;
    return acc;
  }, { qty: 0, pcs: 0, sets: 0, amt: 0, orders: 0 });

  await header(doc, "SKU-WISE REPORT", `${rows.length} designs · ${totals.orders} orders`, rangeLabel);

  autoTable(doc, {
    startY: 170,
    margin: { left: 40, right: 40 },
    head: [["Design #", "PCS", "SETS", "Total Qty", "Value", "Orders"]],
    body: rows.map((r) => [
      r.design_number,
      String(r.total_pcs || 0),
      String(r.total_sets || 0),
      String(r.total_quantity || 0),
      inr(r.total_amount),
      String(r.order_count || 0),
    ]),
    foot: [["TOTAL", String(totals.pcs), String(totals.sets), String(totals.qty), inr(totals.amt), String(totals.orders)]],
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, lineColor: [10, 10, 10], lineWidth: 0.5, textColor: [10, 10, 10] },
    headStyles: { fillColor: [10, 10, 10], textColor: [255, 255, 255], fontStyle: "bold" },
    footStyles: { fillColor: [244, 244, 240], textColor: [10, 10, 10], fontStyle: "bold" },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" }, 5: { halign: "right" } },
  });

  footer(doc, rangeLabel);
  return doc.output("blob");
}

export async function buildAgentReportPdf(rows, rangeLabel) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const totals = rows.reduce((acc, r) => {
    acc.orders += r.total_orders || 0;
    acc.qty += r.total_quantity || 0;
    acc.amt += r.total_amount || 0;
    return acc;
  }, { orders: 0, qty: 0, amt: 0 });

  await header(doc, "AGENT-WISE REPORT", `${rows.length} agents · ${totals.orders} orders`, rangeLabel);

  autoTable(doc, {
    startY: 170,
    margin: { left: 40, right: 40 },
    head: [["Agent", "Orders", "Customers", "Qty", "Value"]],
    body: rows.map((r) => [
      r.agent || "",
      String(r.total_orders || 0),
      String(r.unique_customers || 0),
      String(r.total_quantity || 0),
      inr(r.total_amount),
    ]),
    foot: [["TOTAL", String(totals.orders), "", String(totals.qty), inr(totals.amt)]],
    styles: { font: "helvetica", fontSize: 10, cellPadding: 6, lineColor: [10, 10, 10], lineWidth: 0.5, textColor: [10, 10, 10] },
    headStyles: { fillColor: [10, 10, 10], textColor: [255, 255, 255], fontStyle: "bold" },
    footStyles: { fillColor: [244, 244, 240], textColor: [10, 10, 10], fontStyle: "bold" },
    columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" }, 4: { halign: "right" } },
  });

  // Per-agent customers expansion
  let y = doc.lastAutoTable.finalY + 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(11);
  doc.text("CUSTOMERS PER AGENT", 40, y);
  doc.setDrawColor(10, 10, 10); doc.setLineWidth(1.5);
  doc.line(40, y + 4, doc.internal.pageSize.getWidth() - 40, y + 4);
  y += 18;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  const pageH = doc.internal.pageSize.getHeight();
  rows.forEach((r) => {
    if (y > pageH - 80) { doc.addPage(); y = 60; }
    doc.setFont("helvetica", "bold");
    doc.text(`${r.agent || ""}`, 40, y);
    doc.setFont("helvetica", "normal");
    const list = (r.customers || []).join(", ") || "—";
    const lines = doc.splitTextToSize(list, doc.internal.pageSize.getWidth() - 80);
    doc.text(lines, 40, y + 12);
    y += 12 + lines.length * 11 + 6;
  });

  footer(doc, rangeLabel);
  return doc.output("blob");
}

function footer(doc, rangeLabel) {
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  doc.setDrawColor(10, 10, 10); doc.setLineWidth(0.5);
  doc.line(40, pageH - 30, pageW - 40, pageH - 30);
  doc.setFontSize(8); doc.setTextColor(100, 100, 100);
  doc.text(`Generated ${new Date().toLocaleString()}`, 40, pageH - 18);
  if (rangeLabel) doc.text(rangeLabel, pageW / 2, pageH - 18, { align: "center" });
  doc.text(COMPANY.appName, pageW - 40, pageH - 18, { align: "right" });
}

export function buildReportSummary(kind, rows, rangeLabel) {
  if (kind === "sku") {
    const totals = rows.reduce((a, r) => {
      a.qty += r.total_quantity || 0; a.amt += r.total_amount || 0; a.orders += r.order_count || 0; return a;
    }, { qty: 0, amt: 0, orders: 0 });
    const top = rows.slice(0, 5).map((r, i) =>
      `${i + 1}. ${r.design_number} — ${r.total_quantity} (${r.total_pcs || 0} PCS / ${r.total_sets || 0} SETS) · ₹${Math.round(r.total_amount).toLocaleString("en-IN")}`
    );
    return [
      `*${COMPANY.appName} · SKU REPORT*`,
      rangeLabel ? `Period: ${rangeLabel}` : null,
      ``,
      `Designs: ${rows.length}`,
      `Total Qty: ${totals.qty}`,
      `Total Value: ₹${Math.round(totals.amt).toLocaleString("en-IN")}`,
      `Orders: ${totals.orders}`,
      ``,
      rows.length ? `*Top 5*` : null,
      ...top,
      ``,
      `PDF attached.`,
      ``,
      `_${COMPANY.legalName}_`,
      `Customer Care: ${COMPANY.customerCare} · ${COMPANY.email}`,
    ].filter(Boolean).join("\n");
  }
  const totals = rows.reduce((a, r) => { a.orders += r.total_orders || 0; a.qty += r.total_quantity || 0; a.amt += r.total_amount || 0; return a; }, { orders: 0, qty: 0, amt: 0 });
  const top = rows.slice(0, 5).map((r, i) =>
    `${i + 1}. ${r.agent} — ${r.total_orders} orders · ₹${Math.round(r.total_amount).toLocaleString("en-IN")}`
  );
  return [
    `*${COMPANY.appName} · AGENT REPORT*`,
    rangeLabel ? `Period: ${rangeLabel}` : null,
    ``,
    `Agents: ${rows.length}`,
    `Orders: ${totals.orders}`,
    `Total Qty: ${totals.qty}`,
    `Total Value: ₹${Math.round(totals.amt).toLocaleString("en-IN")}`,
    ``,
    rows.length ? `*Top 5*` : null,
    ...top,
    ``,
    `PDF attached.`,
    ``,
    `_${COMPANY.legalName}_`,
    `Customer Care: ${COMPANY.customerCare} · ${COMPANY.email}`,
  ].filter(Boolean).join("\n");
}

export async function shareReport(kind, rows, rangeLabel, fileName) {
  const blob = kind === "sku" ? await buildSkuReportPdf(rows, rangeLabel) : await buildAgentReportPdf(rows, rangeLabel);
  const file = new File([blob], fileName, { type: "application/pdf" });
  if (typeof navigator !== "undefined" && navigator.canShare && navigator.canShare({ files: [file] })) {
    try {
      await navigator.share({
        title: fileName.replace(/\.pdf$/, ""),
        text: buildReportSummary(kind, rows, rangeLabel),
        files: [file],
      });
      return true;
    } catch (e) { return false; }
  }
  return false;
}

export async function downloadReport(kind, rows, rangeLabel, fileName) {
  const blob = kind === "sku" ? await buildSkuReportPdf(rows, rangeLabel) : await buildAgentReportPdf(rows, rangeLabel);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = fileName;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
